import { getLastPointerPosition } from './pointer-state.js';

const MIN_VISIBLE_RATIO = 0.12;
const POINTER_RECENT_MS = 2_500;

/**
 * Score how "focused" a card is: viewport center + pointer (no client-specific tags).
 * Higher is better; -1 means not a viable candidate.
 */
export function scoreFocusCandidate(
  element: Element,
  intersectionRatio: number,
): number {
  if (intersectionRatio < MIN_VISIBLE_RATIO) return -1;
  if (typeof window === 'undefined') return intersectionRatio;

  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return -1;

  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const viewportCenterX = window.innerWidth / 2;
  const viewportCenterY = window.innerHeight / 2;

  const centerDistance = Math.hypot(
    centerX - viewportCenterX,
    centerY - viewportCenterY,
  );
  const maxCenterDistance =
    Math.hypot(window.innerWidth, window.innerHeight) / 2 || 1;
  const centerScore = 1 - Math.min(1, centerDistance / maxCenterDistance);

  const pointer = getLastPointerPosition();
  let pointerScore = 0;
  const pointerIsRecent =
    pointer != null && Date.now() - pointer.at < POINTER_RECENT_MS;

  if (pointerIsRecent && pointer) {
    const inside =
      pointer.x >= rect.left &&
      pointer.x <= rect.right &&
      pointer.y >= rect.top &&
      pointer.y <= rect.bottom;

    if (inside) {
      pointerScore = 1;
    } else {
      const pointerDistance = Math.hypot(
        pointer.x - centerX,
        pointer.y - centerY,
      );
      const falloff = Math.max(rect.width, rect.height) * 1.25 || 1;
      pointerScore = Math.max(0, 1 - pointerDistance / falloff) * 0.65;
    }
  }

  if (pointerIsRecent) {
    return (
      intersectionRatio * 0.15 + centerScore * 0.25 + pointerScore * 0.6
    );
  }

  return intersectionRatio * 0.25 + centerScore * 0.75;
}

export function pickBestFocusCandidate(
  candidates: Iterable<{ element: Element; ratio: number }>,
): { element: Element; ratio: number; score: number } | null {
  let best: { element: Element; ratio: number; score: number } | null = null;

  for (const { element, ratio } of candidates) {
    const score = scoreFocusCandidate(element, ratio);
    if (score < 0) continue;
    if (!best || score > best.score) {
      best = { element, ratio, score };
    }
  }

  return best;
}
